<template>
    <div
        class="bg-body h-100"
        :class="{
            'has-app-banner': appBannerVisible && showAuthenticatedLayout,
        }"
    >
        <template v-if="showAuthenticatedLayout">
            <AppBanner
                v-if="appBannerVisible"
                @close="isBannerVisible = false"
            ></AppBanner>
            <template v-if="isPdfViewer">
                <AppAlerts></AppAlerts>
                <router-view></router-view>
            </template>
            <AppSidebar
                v-else
                :visible="isSidebarVisible"
                @open="isSidebarVisible = true"
                @close="isSidebarVisible = false"
            >
                <AppNavbar
                    :show-sidebar="isSidebarVisible"
                    @open-sidebar="isSidebarVisible = true"
                    @close-sidebar="isSidebarVisible = false"
                />

                <div class="page-container" :key="globalSiteRefresherKey">
                    <AppAlerts></AppAlerts>

                    <!-- Page header for mobile -->
                    <h1
                        v-if="shouldShowMobilePageHeader"
                        class="col text-start p-0 mb-3"
                    >
                        {{ pageHeaderTitle }}
                    </h1>

                    <router-view></router-view>

                    <!-- View Notifications -->
                    <NotificationPanel></NotificationPanel>

                    <!-- View Messages -->
                    <MessagingPanel></MessagingPanel>

                    <!-- View Patty -->
                    <PattyPanel></PattyPanel>

                    <!-- View Guardians -->
                    <GuardianPanel></GuardianPanel>

                    <!-- "Premim Feature" Modal -->
                    <PremiumModal></PremiumModal>

                    <!-- "Permission Denied" Modal -->
                    <PermissionDeniedModal></PermissionDeniedModal>

                    <ReminderModal></ReminderModal>

                    <ComingSoonModal></ComingSoonModal>

                    <!-- Feedback Modal -->
                    <FeedbackModal />

                    <!-- Release Notes Modal -->
                    <ReleaseNotesModal />

                    <!-- View Audits -->
                    <AuditsPanel :key="auditPanelKey"></AuditsPanel>

                    <!-- Contact Us Button -->
                    <ContactUsButton></ContactUsButton>
                </div>
            </AppSidebar>
        </template>

        <template v-else-if="showPublicLayout">
            <div class="scrollable-panel h-100">
                <AppAlerts></AppAlerts>
                <router-view></router-view>
            </div>
        </template>

        <template v-else>
            <div
                class="h-100 d-flex justify-content-center align-items-center flex-column"
            >
                <div
                    class="spinner-border mb-3"
                    role="status"
                    style="width: 3rem; height: 3rem"
                >
                    <span class="visually-hidden">{{
                        $t('common.states.loading')
                    }}</span>
                </div>
                <h4 style="color: var(--primary-500)">
                    {{ $t('common.states.loading') }}
                </h4>
            </div>
        </template>
    </div>
</template>

<script>
import AppNavbar from '@/components/AppNavbar.vue';
import AppSidebar from '@/components/AppSidebar.vue';
import AppAlerts from '@/components/AppAlerts.vue';
import NotificationPanel from '@/components/NotificationPanel.vue';
import MessagingPanel from '@/components/MessagingPanel.vue';
import PattyPanel from '@/components/PattyPanel.vue';
import GuardianPanel from '@/components/guardians/GuardianPanel.vue';
import PremiumModal from '@/components/PremiumModal.vue';
import PermissionDeniedModal from '@/components/PermissionDeniedModal.vue';
import ComingSoonModal from '@/components/ComingSoonModal.vue';
import AuditsPanel from './components/audits/AuditsPanel.vue';
import AppBanner from './components/AppBanner.vue';
import ContactUsButton from './components/ContactUsButton.vue';
import ReminderModal from '@/components/ReminderModal.vue';
import FeedbackModal from '@/components/FeedbackModal.vue';
import ReleaseNotesModal from '@/components/ReleaseNotesModal.vue';
import { useAdminStore } from '@/stores/admin';
import { encodeGlobalRegionSelection } from '@/components/Region/region';

export default {
    name: 'App',
    components: {
        AppNavbar,
        AppSidebar,
        AppAlerts,
        NotificationPanel,
        MessagingPanel,
        PattyPanel,
        GuardianPanel,
        PremiumModal,
        PermissionDeniedModal,
        ComingSoonModal,
        AuditsPanel,
        AppBanner,
        ContactUsButton,
        ReminderModal,
        FeedbackModal,
        ReleaseNotesModal,
    },
    data() {
        return {
            isAuthenticated: false,
            isSidebarVisible: true,
            adminStore: null,
            isBannerVisible: true,
        };
    },
    computed: {
        showAuthenticatedLayout() {
            // Only show authenticated layout if:
            // 1. Route is not explicitly public (requiresAuth !== false)
            // 2. User is authenticated
            // 3. Route exists (has been resolved by router)
            return (
                this.$route.meta?.requiresAuth !== false &&
                this.$userStore?.isAuthenticated &&
                this.$route &&
                this.$route.matched.length > 0
            );
        },
        showPublicLayout() {
            // Show public layout for explicitly public routes that have been resolved
            return (
                this.$route.meta?.requiresAuth === false &&
                this.$route &&
                this.$route.matched.length > 0
            );
        },
        isPdfViewer() {
            return this.$route.name === 'PdfViewer';
        },
        auditPanelKey() {
            // if the company id is set, use it as the key (to skip zero as falsey)
            if (
                this.adminStore.companyId !== null &&
                this.adminStore.companyId !== undefined
            ) {
                return this.adminStore.companyId;
            }
            return 'default';
        },
        shouldShowAppBanner() {
            return (
                this.$env.environment === 'development' ||
                this.$userStore.isViewOnly
            );
        },
        appBannerVisible() {
            return this.isBannerVisible && this.shouldShowAppBanner;
        },
        pageHeaderTitle() {
            if (this.$route.meta?.headerKey) {
                return this.$t(this.$route.meta.headerKey);
            }

            return this.$route.meta?.header || '';
        },
        shouldShowMobilePageHeader() {
            return (
                this.pageHeaderTitle &&
                this.$isMobile &&
                !this.$route.meta?.hideMobileHeader
            );
        },
        globalSiteRefresherKey() {
            // Skip page refresh key on admin or courses routes
            const skip = this.$route?.path?.startsWith('/admin');

            if (skip) return 'no-site-refresh';

            if (
                this.$siteStore.globalSiteId !== null &&
                this.$siteStore.globalSiteId !== undefined
            ) {
                return 'site-' + this.$siteStore.globalSiteId;
            }

            if (
                this.$route?.name === 'Home' &&
                this.$userStore.hasAlphaAccess &&
                this.$siteStore.globalRegion
            ) {
                return (
                    'region-' +
                    (encodeGlobalRegionSelection(
                        this.$siteStore.globalRegion
                    ) || 'all')
                );
            }
            return 'default';
        },
    },
    watch: {
        $isMobile: {
            immediate: true,
            handler(isMobile) {
                if (isMobile) this.isSidebarVisible = false;
            },
        },
    },
    created() {
        this.adminStore = useAdminStore();
    },
    methods: {},
};
</script>

<style scoped>
.page-container {
    margin-inline: var(--space-10);
    margin-block: var(--space-6) var(--space-10);
}

.scrollable-panel {
    flex: 1 1 0;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    min-width: 0;
}

.has-app-banner {
    padding-top: 2rem; /* height of app banner */
}

@media (max-width: 1200px) {
    .page-container {
        margin-inline: var(--space-4);
        margin-block: var(--space-5);
    }
}

@media only screen and (max-width: 767.98px) {
    .has-app-banner {
        padding-top: 3rem; /* height of app banner */
    }
}
</style>
